import { isEqual, shuffle } from 'lodash';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { apiGetQuestion, apiGetTest, apiGetTestQuestions } from 'core/api';
import { useStableCallback } from 'core/hooks/useStableCallback';

import { TAuxQuestion } from './types';

export function useFetchData() {
  const [{ test, questions, error }, setData] = useState<{
    test: TTest | null;
    questions: TAuxQuestion[];
    error?: string;
  }>({
    test: null,
    questions: [],
  });
  const mode =
    useSearchParams()[0].get('mode') === 'pending' ? 'pending' : 'all';
  const questionUid = useSearchParams()[0].get('question');
  const testUid = orDie(useParams<{ testUid?: string }>().testUid);

  useEffect(() => {
    Promise.all([
      apiGetTest(testUid),
      questionUid
        ? apiGetQuestion(questionUid).then(question => [question])
        : apiGetTestQuestions(testUid, mode),
    ])
      .then(([test, questions]) =>
        setData({
          test,
          questions: shuffle(questions).map((question): TAuxQuestion => {
            const shuffledOptions = shuffle(question.options);
            return {
              ...question,
              options: shuffledOptions,
              rightOption: shuffledOptions.findIndex(
                opt => opt === question.options[0]
              ),
            };
          }),
        })
      )
      .catch((error: unknown) =>
        setData({ test: null, questions: [], error: String(error) })
      );
  }, [testUid]);

  const updateQuestion = useStableCallback((updated: TQuestion) => {
    setData(({ test, questions }) => ({
      test,
      questions: questions.map((question): TAuxQuestion => {
        if (question.uid !== updated.uid) return question;

        // Do not touch the existing options if they are the same to not confuse the user.
        if (
          isEqual([...question.options].sort(), [...updated.options].sort()) &&
          question.options[question.rightOption] === updated.options[0]
        ) {
          return {
            ...updated,
            options: question.options,
            rightOption: question.rightOption,
          };
        }

        return { ...updated, rightOption: 0 };
      }),
    }));
  });

  function deleteQuestion(questionUid: string): void {
    setData(({ test, questions }) => ({
      test,
      questions: questions.filter(q => q.uid !== questionUid),
    }));
  }

  return {
    test,
    questions,
    updateQuestion,
    deleteQuestion,
    error,
    isLoading: !test && !error,
  };
}
